'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { Globe, Plane, Ship, ShieldCheck, CheckCircle2, FileText, Send } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function ExportPage() {
  const [formData, setFormData] = useState({
    importerName: '',
    companyName: '',
    country: 'United States',
    portOfDischarge: '',
    email: '',
    phone: '',
    transportMode: 'Air Cargo Express',
    estimatedVolume: '100 - 250 Cartons',
    notes: '',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const apiBase = process.env.NEXT_PUBLIC_API_URL || 'https://elavarasiapi.orgininfo.com/api';
    fetch(`${apiBase}/v1/export/inquiry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    }).catch(console.error);

    setSubmitted(true);
  };

  const countries = [
    { name: 'United States & Canada', code: 'US/CA', reg: 'US FDA Registered & Compliant labeling', flag: '🇺🇸' },
    { name: 'United Kingdom & Europe', code: 'UK/EU', reg: 'HACCP & EC 852/2004 standards', flag: '🇬🇧' },
    { name: 'United Arab Emirates & GCC', code: 'UAE/GCC', reg: 'Halal certified facilities & Arabic bilingual labels', flag: '🇦🇪' },
    { name: 'Singapore & Malaysia', code: 'SG/MY', reg: 'SFA Singapore & Agrobazaar standard compliance', flag: '🇸🇬' },
    { name: 'Australia & New Zealand', code: 'AU/NZ', reg: 'Biosecurity & Department of Agriculture compliant', flag: '🇦🇺' },
  ];

  return (
    <main className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* 1. Export Hero */}
      <section className="bg-[#0A261A] text-white py-14 sm:py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <div className="inline-flex items-center space-x-2 px-3.5 py-1 rounded-full bg-white/10 border border-[#F5B52E]/40 text-[#F5B52E] text-xs font-bold tracking-wider uppercase">
            <Globe className="w-3.5 h-3.5" />
            <span>International Trade & Cargo Division</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-tight">
            GLOBAL EXPORT OF <br />
            <span className="text-[#F5B52E]">SOUTH INDIAN SNACKS</span>
          </h1>

          <p className="text-sm sm:text-base text-neutral-300 max-w-2xl mx-auto font-medium leading-relaxed">
            Bringing authentic wood-fired Nendran banana chips, Maravalli tapioca crisps, and Kai Murukku
            to the Indian diaspora worldwide. Certified export-grade production and 90-day crunch-lock technology.
          </p>

          <div className="pt-2">
            <a
              href="#export-rfq"
              className="px-6 py-3 rounded-full bg-[#F5B52E] hover:bg-[#ffc34d] text-[#0A261A] font-bold text-xs sm:text-sm uppercase tracking-wider transition-all shadow-md inline-block"
            >
              Connect with Export Desk
            </a>
          </div>
        </div>
      </section>

      {/* 2. Export Markets */}
      <section className="py-14 bg-white border-b border-neutral-150">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="text-center space-y-2">
            <h2 className="text-2xl sm:text-3xl font-black text-neutral-900 uppercase tracking-tight">
              KEY EXPORT DESTINATIONS
            </h2>
            <div className="h-0.5 w-12 bg-emerald-600 rounded-full mx-auto mt-2"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {countries.map((c, i) => (
              <div key={i} className="p-6 rounded-2xl bg-[#FAFBF9] border border-neutral-200/80 space-y-2">
                <div className="text-3xl">{c.flag}</div>
                <h3 className="text-base font-bold text-neutral-900">{c.name}</h3>
                <p className="text-xs text-neutral-500 font-medium">{c.reg}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Export Standards & Logistics */}
      <section className="py-12 bg-[#FAFBF9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="text-center">
            <h2 className="text-2xl font-black text-neutral-900 uppercase tracking-tight">
              INTERNATIONAL QUALITY COMPLIANCE
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-white rounded-2xl border border-neutral-200/80 space-y-2">
              <ShieldCheck className="w-6 h-6 text-emerald-700" />
              <h4 className="text-sm font-bold text-neutral-900">Food Safety Certifications</h4>
              <p className="text-xs text-neutral-600 font-medium">FSSAI Export Certified, ISO 22000, and HACCP facility. Lab certificates of analysis (COA) provided with every sea and air shipment.</p>
            </div>
            <div className="p-6 bg-white rounded-2xl border border-neutral-200/80 space-y-2">
              <FileText className="w-6 h-6 text-emerald-700" />
              <h4 className="text-sm font-bold text-neutral-900">Export Documentation</h4>
              <p className="text-xs text-neutral-600 font-medium">Complete export clearances including Bill of Lading, Certificate of Origin, Phytosanitary Inspection, and custom HS Code classification.</p>
            </div>
            <div className="p-6 bg-white rounded-2xl border border-neutral-200/80 space-y-2">
              <Plane className="w-6 h-6 text-emerald-700" />
              <h4 className="text-sm font-bold text-neutral-900">Air & Sea Freight Logistics</h4>
              <p className="text-xs text-neutral-600 font-medium">FOB, CIF, and DDP shipments via Tuticorin, Chennai, and Cochin sea ports, or rapid air cargo for festive seasonal demands.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Export RFQ Form */}
      <section id="export-rfq" className="py-14 bg-white flex-1">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FAFBF9] border border-neutral-200/90 rounded-3xl p-6 sm:p-10 shadow-sm space-y-6">
            <div className="text-center space-y-1.5">
              <h3 className="text-2xl font-black text-neutral-900 uppercase tracking-tight">
                International Trade Inquiry
              </h3>
              <p className="text-xs text-neutral-500 font-medium">
                Reach our export desk for container volume price lists and destination compliance guidance.
              </p>
            </div>

            {submitted ? (
              <div className="p-8 rounded-2xl bg-emerald-50 border border-emerald-200 text-center space-y-2">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                <h4 className="text-base font-bold text-emerald-950">Inquiry Received by Export Desk!</h4>
                <p className="text-xs text-emerald-800">
                  Our international trade officer will review your port requirements and email you the CIF/FOB price catalog within 6 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Company / Importer Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      placeholder="e.g. Patel Brothers, UK Traders"
                      className="w-full px-3 py-2 text-xs bg-white border border-neutral-300 rounded-xl focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Contact Officer Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.importerName}
                      onChange={(e) => setFormData({ ...formData, importerName: e.target.value })}
                      className="w-full px-3 py-2 text-xs bg-white border border-neutral-300 rounded-xl focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Destination Country *</label>
                    <select
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="w-full px-3 py-2 text-xs bg-white border border-neutral-300 rounded-xl focus:outline-none focus:border-emerald-600"
                    >
                      <option>United States</option>
                      <option>United Kingdom</option>
                      <option>United Arab Emirates</option>
                      <option>Singapore</option>
                      <option>Malaysia</option>
                      <option>Australia</option>
                      <option>Canada</option>
                      <option>Germany / EU</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Port of Discharge / Airport</label>
                    <input
                      type="text"
                      value={formData.portOfDischarge}
                      onChange={(e) => setFormData({ ...formData, portOfDischarge: e.target.value })}
                      placeholder="e.g. Port of New York, Heathrow"
                      className="w-full px-3 py-2 text-xs bg-white border border-neutral-300 rounded-xl focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 text-xs bg-white border border-neutral-300 rounded-xl focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Phone / WhatsApp with Country Code *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 (555) 000-0000"
                      className="w-full px-3 py-2 text-xs bg-white border border-neutral-300 rounded-xl focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-3.5 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs uppercase tracking-wider shadow-md transition-all cursor-pointer flex items-center justify-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>Submit Export RFQ</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
